import { isOnline } from '@const/env';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { resolveUnlimitedAnonymousProjects } from './helper';
import { type DebugSettings, PromotionDebugMode } from './types';

interface DebugState extends DebugSettings {
  isDebugPanelOpen: boolean;
  showChangelogModal: boolean;
  showCreditCareModal: boolean;
  setNotifyOverride: (notifyName: string, value: boolean) => void;
  clearNotifyOverride: (notifyName: string) => void;
  setCustomApiBaseUrl: (url: string) => void;
  setUseCustomApiBaseUrl: (use: boolean) => void;
  setCustomCookie: (cookie: string) => void;
  setUseCustomCookie: (use: boolean) => void;
  setHideAppInfo: (hide: boolean) => void;
  setEnableLocalCollect: (enable: boolean) => void;
  setShowPreviewDebugInfo: (show: boolean) => void;
  setEnablePerformanceDebug: (enable: boolean) => void;
  setShowABTestPanel: (show: boolean) => void;
  setEnableClientABTest: (enable: boolean) => void;
  setPromotionDebugMode: (mode: PromotionDebugMode) => void;
  setUnlimitedAnonymousProjects: (unlimited: boolean) => void;
  setIsDebugPanelOpen: (open: boolean) => void;
  setShowChangelogModal: (show: boolean) => void;
  setShowCreditCareModal: (show: boolean) => void;
  toggleDebugPanel: () => void;
}

const initialState: DebugSettings = {
  customApiBaseUrl: '',
  useCustomApiBaseUrl: false,
  customCookie: '',
  useCustomCookie: false,
  hideAppInfo: false,
  enableLocalCollect: true,
  showPreviewDebugInfo: false,
  enablePerformanceDebug: false,
  showABTestPanel: false,
  enableClientABTest: false,
  promotionDebugMode: PromotionDebugMode.Normal,
  unlimitedAnonymousProjects: false,
  notifyOverrides: {},
};

export function selectUnlimitedAnonymousProjects(state: Pick<DebugSettings, 'unlimitedAnonymousProjects'>) {
  return resolveUnlimitedAnonymousProjects(isOnline, state.unlimitedAnonymousProjects);
}

export const useDebugStore = create<DebugState>()(
  persist(
    set => ({
      ...initialState,
      isDebugPanelOpen: false,
      showChangelogModal: false,
      showCreditCareModal: false,

      setNotifyOverride: (notifyName, value) =>
        set(state => ({ notifyOverrides: { ...state.notifyOverrides, [notifyName]: value } })),
      clearNotifyOverride: (notifyName) =>
        set((state) => {
          const { [notifyName]: _, ...rest } = state.notifyOverrides;
          return { notifyOverrides: rest };
        }),

      setCustomApiBaseUrl: url => set({ customApiBaseUrl: url }),
      setUseCustomApiBaseUrl: use => set({ useCustomApiBaseUrl: use }),
      setCustomCookie: cookie => set({ customCookie: cookie }),
      setUseCustomCookie: use => set({ useCustomCookie: use }),
      setHideAppInfo: hide => set({ hideAppInfo: hide }),
      setEnableLocalCollect: enable => set({ enableLocalCollect: enable }),
      setShowPreviewDebugInfo: show => set({ showPreviewDebugInfo: show }),
      setEnablePerformanceDebug: enable => set({ enablePerformanceDebug: enable }),
      setShowABTestPanel: show => set({ showABTestPanel: show }),
      setEnableClientABTest: enable => set({ enableClientABTest: enable }),
      setPromotionDebugMode: mode => set({ promotionDebugMode: mode }),
      setUnlimitedAnonymousProjects: unlimited => set({ unlimitedAnonymousProjects: unlimited }),
      setIsDebugPanelOpen: open => set({ isDebugPanelOpen: open }),
      setShowChangelogModal: show => set({ showChangelogModal: show }),
      setShowCreditCareModal: show => set({ showCreditCareModal: show }),
      toggleDebugPanel: () => set(state => ({ isDebugPanelOpen: !state.isDebugPanelOpen })),
    }),
    {
      name: 'debug-settings',
      partialize: state => ({
        customApiBaseUrl: state.customApiBaseUrl,
        useCustomApiBaseUrl: state.useCustomApiBaseUrl,
        customCookie: state.customCookie,
        useCustomCookie: state.useCustomCookie,
        hideAppInfo: state.hideAppInfo,
        enableLocalCollect: state.enableLocalCollect,
        showPreviewDebugInfo: state.showPreviewDebugInfo,
        enablePerformanceDebug: state.enablePerformanceDebug,
        showABTestPanel: state.showABTestPanel,
        enableClientABTest: state.enableClientABTest,
        promotionDebugMode: state.promotionDebugMode,
        unlimitedAnonymousProjects: state.unlimitedAnonymousProjects,
        notifyOverrides: state.notifyOverrides,
      }),
    },
  ),
);

export * from './types';
